import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { sanitizeIP, sanitizeAPIKey } from '../utils/sanitize';
// import { BannerAd } from '../components/GoogleAd'; // Uncomment when ready
import { 
  Key, 
  Copy, 
  Eye, 
  EyeOff, 
  Plus, 
  AlertCircle, 
  CheckCircle,
  Lock,
  Unlock,
  Trash2
} from 'lucide-react';
import './ApiKeys.css';

interface APIKeyData {
  id: string;
  key: string;
  plan: string;
  status: string;
  usage_stats: {
    total_requests: number;
    total_tokens: number;
  };
  limits: {
    max_tokens_per_request: number;
    max_ips: number;
    requests_per_minute: number;
  };
  ip_lock: {
    enabled: boolean;
    enforced: boolean;
    locked_ips: string[];
    max_ips: number;
  };
  rate_limit_windows: {
    current_minute: string;
    requests_this_minute: number;
  };
  metadata: {
    created_at: string;
    expires_at?: string;
    owner_id: string;
  };
}

const ApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<APIKeyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showIPModal, setShowIPModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKeyData | null>(null);
  const [lockedIPs, setLockedIPs] = useState<string[]>([]);
  const [newIP, setNewIP] = useState('');
  const [ipLoading, setIPLoading] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getKeys();
      setKeys(response.data.keys || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchIPsForKey = async (apiKey: string) => {
    try {
      setIPLoading(true);
      const response = await userAPI.getIPs(apiKey);
      setLockedIPs(response.data.ips || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load IPs');
    } finally {
      setIPLoading(false);
    }
  };

  const handleAddIP = async (apiKey: string, ipAddress: string) => {
    try {
      setIPLoading(true);
      
      // Sanitize IP before sending
      const sanitizedIP = sanitizeIP(ipAddress.trim());
      const sanitizedKey = sanitizeAPIKey(apiKey);
      
      await userAPI.addIP(sanitizedKey, sanitizedIP);
      setSuccess('IP added successfully!');
      fetchIPsForKey(apiKey);
      setNewIP('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add IP');
    } finally {
      setIPLoading(false);
    }
  };

  const detectClientIP = async (): Promise<string> => {
    // Try Cloudflare trace first (most reliable since app uses Cloudflare)
    try {
      console.log('🔍 Detecting IP via Cloudflare trace...');
      const response = await fetch('https://1.1.1.1/cdn-cgi/trace', {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        const text = await response.text();
        const ipMatch = text.match(/ip=([\d.]+)/);
        if (ipMatch && ipMatch[1]) {
          console.log(`✅ Detected IP: ${ipMatch[1]} from Cloudflare`);
          return ipMatch[1];
        }
      }
    } catch (error) {
      console.warn('Cloudflare trace failed:', error);
    }

    // Fallback to other services
    const services = [
      { url: 'https://api.ipify.org?format=json', key: 'ip' },
      { url: 'https://ipapi.co/json/', key: 'ip' },
      { url: 'https://icanhazip.com', key: null },
    ];

    for (const service of services) {
      try {
        console.log(`Trying IP detection: ${service.url}`);
        const response = await fetch(service.url, { 
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });
        
        if (!response.ok) continue;
        
        if (service.key) {
          const data = await response.json();
          const ip = data[service.key];
          if (ip && /^\d+\.\d+\.\d+\.\d+$/.test(ip)) {
            console.log(`✅ Detected IP: ${ip} from ${service.url}`);
            return ip;
          }
        } else {
          const ip = (await response.text()).trim();
          if (ip && /^\d+\.\d+\.\d+\.\d+$/.test(ip)) {
            console.log(`✅ Detected IP: ${ip} from ${service.url}`);
            return ip;
          }
        }
      } catch (error) {
        console.warn(`Failed: ${service.url}`, error);
        continue;
      }
    }
    
    throw new Error('Could not detect your IP address. Please enter it manually.');
  };

  const handleAddCurrentIP = async (apiKey: string) => {
    try {
      setIPLoading(true);
      
      // Detect client IP from frontend
      const clientIP = await detectClientIP();
      
      // Use regular addIP with detected IP
      await handleAddIP(apiKey, clientIP);
      setSuccess(`Added your IP: ${clientIP}`);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to add current IP');
    } finally {
      setIPLoading(false);
    }
  };

  const handleRemoveIP = async (apiKey: string, ipAddress: string) => {
    if (!window.confirm(`Remove IP ${ipAddress}?`)) return;
    
    try {
      setIPLoading(true);
      await userAPI.removeIP(apiKey, ipAddress);
      setSuccess('IP removed successfully!');
      fetchIPsForKey(apiKey);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove IP');
    } finally {
      setIPLoading(false);
    }
  };

  const handleRedeemKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setRedeeming(true);

    try {
      // Sanitize redeem code
      const sanitizedCode = sanitizeAPIKey(redeemCode.trim());
      
      if (!sanitizedCode) {
        throw new Error('Invalid redeem code format');
      }
      
      await userAPI.redeemKey(sanitizedCode);
      setSuccess('API key redeemed successfully!');
      setShowRedeemModal(false);
      setRedeemCode('');
      fetchKeys();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to redeem key');
    } finally {
      setRedeeming(false);
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskKey = (key: string) => {
    if (key.length <= 8) return key;
    return key.substring(0, 8) + '•'.repeat(20) + key.substring(key.length - 8);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'free': return 'info';
      case 'pro': return 'success';
      case 'team': return 'warning';
      case 'enterprise': return 'primary';
      default: return 'info';
    }
  };

  // TESTING MODE: Show ads to ALL users
  // const isFreeUser = true; // Uncomment when ads are ready

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading" style={{ width: '3rem', height: '3rem' }}></div>
      </div>
    );
  }

  return (
    <div className="api-keys-page fade-in">
      {/* 📢 BANNER AD */}
      {/* {isFreeUser && <BannerAd />} */}
      
      <div className="page-header">
        <div>
          <h1>API Keys</h1>
          <p>Manage your API keys and access tokens</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowRedeemModal(true)}
        >
          <Plus size={20} />
          Redeem New Key
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* API Keys List */}
      {keys.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Key size={64} style={{ color: 'var(--color-text-tertiary)' }} />
            <h2>No API Keys</h2>
            <p>You haven't redeemed any API keys yet. Click the button above to get started.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowRedeemModal(true)}
            >
              Redeem Your First Key
            </button>
          </div>
        </div>
      ) : (
        <div className="keys-grid">
          {keys.map((keyData) => {
            const isVisible = visibleKeys.has(keyData.id);
            const isCopied = copiedKey === keyData.id;
            
            return (
              <div key={keyData.id} className="key-card">
                <div className="key-card-header">
                  <div className="key-card-title">
                    <Key size={20} />
                    <span>API Key</span>
                  </div>
                  <div className={`badge badge-${getPlanColor(keyData.plan)}`}>
                    {keyData.plan}
                  </div>
                </div>

                <div className="key-display">
                  <code className="key-value">
                    {isVisible ? keyData.key : maskKey(keyData.key)}
                  </code>
                  <div className="key-actions">
                    <button
                      className="btn-icon"
                      onClick={() => toggleKeyVisibility(keyData.id)}
                      title={isVisible ? 'Hide' : 'Show'}
                    >
                      {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      className={`btn-icon ${isCopied ? 'success' : ''}`}
                      onClick={() => copyToClipboard(keyData.key, keyData.id)}
                      title="Copy"
                    >
                      {isCopied ? <CheckCircle size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>

                <div className="key-stats">
                  <div className="stat-item">
                    <span className="stat-label">Requests</span>
                    <span className="stat-value">{keyData.usage_stats?.total_requests || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Tokens</span>
                    <span className="stat-value">{keyData.usage_stats?.total_tokens || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Rate Limit</span>
                    <span className="stat-value">{keyData.limits?.requests_per_minute || 0}/min</span>
                  </div>
                </div>

                <div className="key-details">
                  <div className="detail-row">
                    <span className="detail-label">
                      <Lock size={14} />
                      Locked IPs
                    </span>
                    <span className="detail-value">
                      {keyData.ip_lock?.locked_ips?.length || 0} / {keyData.ip_lock?.max_ips || 0}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Max Tokens</span>
                    <span className="detail-value">
                      {keyData.limits?.max_tokens_per_request?.toLocaleString() || 'N/A'}
                    </span>
                  </div>
                  {keyData.metadata?.expires_at && (
                    <div className="detail-row">
                      <span className="detail-label">Expires</span>
                      <span className="detail-value">
                        {formatDate(keyData.metadata.expires_at)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="key-card-footer">
                  <div className={`status-indicator ${keyData.status}`}>
                    {keyData.status === 'active' ? <Unlock size={14} /> : <Lock size={14} />}
                    {keyData.status}
                  </div>
                  <button 
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      setSelectedKey(keyData);
                      setShowIPModal(true);
                      fetchIPsForKey(keyData.key);
                    }}
                  >
                    <Lock size={14} />
                    Manage IPs
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* IP Management Modal */}
      {showIPModal && selectedKey && (
        <div className="modal-overlay" onClick={() => setShowIPModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Lock size={24} />
                Manage IP Whitelist
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowIPModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="ip-info">
                <p><strong>API Key:</strong> <code>{maskKey(selectedKey.key)}</code></p>
                <p><strong>Plan:</strong> <span className={`badge badge-${getPlanColor(selectedKey.plan)}`}>{selectedKey.plan}</span></p>
                <p><strong>IP Slots:</strong> {lockedIPs.length} / {selectedKey.limits?.max_ips || 0}</p>
              </div>

              {/* Add IP Form */}
              <div className="form-group">
                <label>Add New IP Address</label>
                <div className="input-group">
                  <input
                    type="text"
                    value={newIP}
                    onChange={(e) => setNewIP(e.target.value)}
                    placeholder="192.168.1.1"
                    disabled={ipLoading || lockedIPs.length >= (selectedKey.limits?.max_ips || 0)}
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleAddIP(selectedKey.key, newIP)}
                    disabled={ipLoading || !newIP || lockedIPs.length >= (selectedKey.limits?.max_ips || 0)}
                  >
                    <Plus size={18} />
                    Add IP
                  </button>
                </div>
                <button 
                  className="btn btn-outline btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => handleAddCurrentIP(selectedKey.key)}
                  disabled={ipLoading || lockedIPs.length >= (selectedKey.limits?.max_ips || 0)}
                >
                  <Plus size={14} />
                  Add My Current IP
                </button>
              </div>

              {/* Locked IPs List */}
              <div className="locked-ips-list">
                <h4>Locked IP Addresses ({lockedIPs.length})</h4>
                {ipLoading ? (
                  <div className="loading-container">
                    <div className="loading"></div>
                  </div>
                ) : lockedIPs.length === 0 ? (
                  <div className="empty-state-small">
                    <Lock size={32} />
                    <p>No IP addresses locked yet</p>
                  </div>
                ) : (
                  <div className="ip-items">
                    {lockedIPs.map((ip, index) => (
                      <div key={index} className="ip-item">
                        <span className="ip-address">
                          <Lock size={14} />
                          {ip}
                        </span>
                        <button 
                          className="btn-icon btn-icon-danger"
                          onClick={() => handleRemoveIP(selectedKey.key, ip)}
                          disabled={ipLoading}
                          title="Remove IP"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-outline"
                onClick={() => setShowIPModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redeem Key Modal */}
      {showRedeemModal && (
        <div className="modal-overlay" onClick={() => setShowRedeemModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Redeem API Key</h2>
              <button 
                className="modal-close"
                onClick={() => setShowRedeemModal(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleRedeemKey}>
              <div className="form-group">
                <label htmlFor="redeemCode">
                  <Key size={18} />
                  Redeem Code
                </label>
                <input
                  id="redeemCode"
                  type="text"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value)}
                  placeholder="Enter your redeem code"
                  required
                  disabled={redeeming}
                />
                <small className="form-help">
                  Enter the redeem code you received after purchase
                </small>
              </div>

              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowRedeemModal(false)}
                  disabled={redeeming}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={redeeming || !redeemCode}
                >
                  {redeeming ? (
                    <>
                      <div className="loading"></div>
                      Redeeming...
                    </>
                  ) : (
                    'Redeem Key'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;